import React, { useContext, useEffect, useState } from 'react';

import { Checkbox, Skeleton } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { ReactComponent as Chevron } from '../../../assets/icons/chevron-down-black.svg';
import { ReactComponent as MinusIcon } from '../../../assets/icons/minus-circle.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus-circle.svg';
import { HIDE_ROW_STATE, LOADING_TABLE_ROWS, SHOW_ROW_STATE } from '../../constants/constants';
import { CompanyContext } from '../../context/company/companyContext';
import { IRowStatus } from '../../interfaces/collaboratorRow';
import { ITableColumn } from '../../interfaces/tables';
import { TRANSLATIONS_GLOBAL } from '../../translations/commons';
import { GenericObject, GenericVoidFunction } from '../../types/genericTypes';
import CardStatus from '../CardStatus/CardStatus';
import StyledButton from '../StyledButton/StyledButton';

import './styles.scss';

interface IStyledTableProps {
  columns: ITableColumn[];
  data: GenericObject[] | undefined;
  emptyKey?: string;
  emptyPath?: string;
  error: boolean;
  errorAction?: GenericVoidFunction;
  showMore?: boolean;
  startRowState?: IRowStatus;
  onCheck?: GenericVoidFunction;
  sortData?: GenericVoidFunction;
  fixedHeight?: boolean;
}

interface ITableRow {
  row: GenericObject;
  columns: ITableColumn[];
  showMore: boolean;
  startRowState: IRowStatus;
  checked?: boolean;
  onCheck?: GenericVoidFunction;
  onClick?: GenericVoidFunction;
}

interface ISelectedMap {
  [key: string]: boolean;
}

const TableRow = ({ row, columns, showMore, startRowState, checked, onCheck, onClick }: ITableRow) => {
  const [rowState, setRowState] = useState<IRowStatus>(startRowState);

  const { t } = useTranslation();

  const renderData = (columnDetail: ITableColumn): string | any => {
    if (columnDetail.render) {
      return columnDetail.render(row);
    }
    return t(row[columnDetail.key]);
  };

  const onButtonClick = () => {
    if (rowState.state === 'hide-row') {
      setRowState(SHOW_ROW_STATE);
    } else {
      setRowState(HIDE_ROW_STATE);
    }
  };

  return (
    <>
      <tr className={`table-row ${onClick !== undefined ? 'hover-enabled' : ''}`} onClick={onClick}>
        {onCheck && (
          <td className="styled-table-column">
            <Checkbox className="checkbox" checked={checked} onChange={onCheck} />
          </td>
        )}
        {columns.map((column) => (
          <td className={column.class} key={uuid()}>
            {renderData(column)}
          </td>
        ))}
        {showMore && (
          <td className="tablet-mobile-only data">
            <button className="icon toggle-info" onClick={onButtonClick}>
              <PlusIcon className={rowState.plusClass} data-testid="plus-icon" />
              <MinusIcon className={rowState.minusClass} data-testid="minus-icon" />
            </button>
          </td>
        )}
      </tr>
      <tr className="tablet-mobile-only row">
        <td colSpan={6}>
          <div className={`collapsable-section ${rowState.state}`}>
            {columns.map((column) => (
              <div className={`section-container ${column.class}`} key={uuid()}>
                <div className={`item-title ${column.key}`}>{t(column.name)}</div>
                <div className={`item-value ${column.key}`}>{renderData(column)}</div>
              </div>
            ))}
          </div>
        </td>
      </tr>
    </>
  );
};

const StyledTable = ({
  columns,
  data,
  emptyKey,
  emptyPath = '/',
  error,
  errorAction,
  showMore = true,
  startRowState = HIDE_ROW_STATE,
  fixedHeight = false,
  onCheck,
  sortData,
  children
}: React.PropsWithChildren<IStyledTableProps>) => {
  const { t } = useTranslation();
  const { companyData, isLoadingCompanyData } = useContext(CompanyContext);
  const [selectedMap, setSelectedMap] = useState<ISelectedMap>({});
  const [columnsByCountry, setColumnsByCountry] = useState<ITableColumn[]>([]);
  const [isAscending, setIsAscending] = useState<boolean[]>([]);

  useEffect(() => {
    if (!onCheck || !data) return;
    setSelectedMap(
      data.reduce((map, obj) => {
        map[obj.id] = false;
        return map;
      }, {})
    );
    onCheck([]);
  }, [data]);

  useEffect(() => {
    setColumnsByCountry(filterColumnsByCountry());
  }, [companyData, columns]);

  useEffect(() => {
    const newIsAscending = columnsByCountry.map(() => false);
    setIsAscending(newIsAscending);
  }, [columnsByCountry]);

  const filterColumnsByCountry = () =>
    columns.filter((column) => !column?.hideIn || !column.hideIn.includes(companyData?.locale.toLowerCase()));

  const reloadPage = () => {
    window.location.reload();
  };

  const renderSkeletons = () =>
    [...Array(LOADING_TABLE_ROWS).keys()].map(() => (
      <Skeleton className="table-skeleton" key={uuid()} animation="wave" variant="text" width={'100%'} height={60} />
    ));

  const renderEmptyView = () => (
    <Trans
      i18nKey={emptyKey}
      components={{
        b2bBold: <b></b>,
        b2bBreak: <div></div>,
        b2bLink: <Link to={{ pathname: emptyPath }} />
      }}
    />
  );

  const showSkeletons = () => isLoadingCompanyData || (!data && !error);

  const showEmptyTable = () => data && !data.length && !error;

  const showErrorTable = () => error;

  const showTable = () => data?.length && !showSkeletons();

  const onSelectAll = () => {
    if (!onCheck || !data) return;
    const areAllSelected = Object.values(selectedMap).every((value) => value);
    const newMap = data.reduce((map, obj) => {
      map[obj.id] = !areAllSelected;
      return map;
    }, {});
    setSelectedMap(newMap);
    onCheck(Object.keys(newMap).filter((key) => newMap[key]));
  };

  const onCheckRow = (key: string) => {
    if (!onCheck) return;
    const newMap = { ...selectedMap, [key]: !selectedMap[key] };
    setSelectedMap(newMap);
    onCheck(Object.keys(newMap).filter((key) => newMap[key]));
  };

  const onSort = (column: ITableColumn, index: number) => {
    if (!column.isSortable || !sortData) return;
    const newIsAscending = [...isAscending];
    newIsAscending[index] = !newIsAscending[index];
    setIsAscending(newIsAscending);
    sortData(column.key);
  };

  return (
    <div className={`table-content-container ${fixedHeight ? 'fixed-height' : ''}`}>
      <table className="custom-table">
        <thead className="table-header whitespace-nowrap">
          <tr>
            {showSkeletons() ? (
              <th>
                <Skeleton className="table-skeleton" animation="wave" variant="text" width={'100%'} height={60} />
              </th>
            ) : (
              <>
                {onCheck && (
                  <th className="styled-table-column">
                    <Checkbox
                      className="checkbox"
                      checked={Object.values(selectedMap).every((value) => value)}
                      onChange={onSelectAll}
                    />
                  </th>
                )}
                {columnsByCountry.map((column, index) => (
                  <th
                    className={`styled-table-column ${column.class} ${
                      column.isSortable ? 'cursor-pointer' : 'cursor-default'
                    }`}
                    key={`${column.name}-${uuid()}`}
                    onClick={() => onSort(column, index)}
                  >
                    {t(column.name)}
                    {column.isSortable && <Chevron className={`sort-chevron ${isAscending[index] ? 'asc' : 'desc'}`} />}
                  </th>
                ))}
              </>
            )}
          </tr>
          {children && (
            <tr className="toolbar">
              <th colSpan={columns.length + 1}>{children}</th>
            </tr>
          )}
        </thead>

        <tbody className="table-body">
          {!!showTable() &&
            data?.map((row) => (
              <TableRow
                row={row}
                columns={columnsByCountry}
                key={row.id ?? uuid()}
                showMore={showMore}
                startRowState={startRowState}
                {...(onCheck && {
                  checked: !!selectedMap[row.id],
                  onCheck: () => onCheckRow(row.id)
                })}
                onClick={row.onClick}
              />
            ))}
        </tbody>
      </table>

      {!showTable() && (
        <div className="empty-table">
          {showSkeletons() && renderSkeletons()}
          {showEmptyTable() && (
            <div>
              <CardStatus type="informative" message={renderEmptyView()} />
            </div>
          )}

          {showErrorTable() && (
            <>
              <div className="error-title">
                {t(TRANSLATIONS_GLOBAL.table.errorTitleOne).concat(' ', t(TRANSLATIONS_GLOBAL.table.errorTitleTwo))}
              </div>
              <div className="error-subtitle">{t(TRANSLATIONS_GLOBAL.table.errorSubtitle)}</div>
              <StyledButton
                classes="error-button"
                buttonType="secondary"
                action={errorAction ? errorAction : reloadPage}
              >
                {t(TRANSLATIONS_GLOBAL.table.button)}
              </StyledButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default StyledTable;
